import React, { useEffect, useRef, useState } from "react";
import { useRecordContext } from "react-admin";
import * as d3 from "d3";
import { bwColours } from "../lib/theme";
import { useResizeObserver } from "./utils";
import { fetchJson } from "../lib/httpClient";

export const HistoryChart = ({
  yLabel,
  field,
  actuationStates = [], // keys of the actuation state telemetry to display
  yMin,
  yMax,
  formatValue,
  colour,
  style,
  convertValue = undefined, // optional (value) => displayValue transform, e.g. C -> F
  dataSeries = undefined, // when provided, skip fetching the main data series
}) => {
  const record = useRecordContext();
  const [data, setData] = useState(null);
  const [actuations, setActuations] = useState(null);

  // Joined so a fresh array prop doesn't retrigger the fetch
  const actuationStatesKey = actuationStates.join(",");

  // Style map for actuation keys — centralises colour/opacity per key.
  // This should be consistent with charts/ferments.tsx
  const styleFor = (key: string) => {
    switch (key) {
      case "cooling_state":
        return { colour: bwColours.accent.blue, opacity: 0.12 };
      case "heating_state":
        return { colour: bwColours.accent.red, opacity: 0.12 };
      case "pumpover_state":
        return { colour: bwColours.primary.offblack, opacity: 0.2 };
      default:
        return { colour: bwColours.accent.orange, opacity: 0.1 };
    }
  };

  useEffect(() => {
    if (record) {
      if (dataSeries === undefined) {
        fetchJson(
          `/api/v2/c/${record.customerID}/smart_tanks/${record.id}/history/${field}`,
        ).then((response) => {
          setData(response.json);
        });
      }
      if (actuationStatesKey) {
        Promise.all(
          actuationStates.map((key) =>
            fetchJson(
              `/api/v2/c/${record.customerID}/smart_tanks/${record.id}/history/${key}`,
            ),
          ),
        ).then((responses) => {
          setActuations(
            responses.map((r, _i) => ({
              series: r.json,
              ...styleFor(actuationStates[_i]),
            })),
          );
        });
      }
    }
  }, [record, field, actuationStatesKey, actuationStates, dataSeries]);

  return (
    <HistoryPlot
      yLabel={yLabel}
      yMin={yMin}
      yMax={yMax}
      formatValue={formatValue}
      colour={colour}
      style={style}
      convertValue={convertValue}
      dataSeries={data}
      actuations={actuations}
    />
  );
};

// FIXME: Trying to harmonize this with FermentPlot
const HistoryPlot = ({
  formatValue,
  yMin,
  yMax,
  yLabel,
  colour,
  style,
  convertValue = undefined, // optional (value) => displayValue transform, e.g. C -> F
  dataSeries, // should be an array of {x: [Date], y: [value]}
  actuations, // actuation state displays: {series: {x: [Date], y: [value]},
  // colour, opacity}; the hours in which an actuation was active are shown as
  // bands of the given colour at the given opacity
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(wrapperRef);

  useEffect(() => {
    if (!dimensions) return;

    // Convert stored values to the display unit for axes, curve and tooltip.
    const toDisplay = (v) => (convertValue ? convertValue(v) : v);

    const margin = {
      top: 30,
      left: 60,
      bottom: 30,
      right: 20,
    };

    let canvas = d3.select(svgRef.current);
    canvas.selectAll("*").remove();

    // Don't render if the effective width is too small
    if (dimensions.width - margin.left - margin.right <= 0) {
      return;
    }

    // chart background
    canvas
      .append("rect")
      .attr("x", margin.left)
      .attr("y", margin.top)
      .attr("width", dimensions.width - margin.left - margin.right)
      .attr("height", dimensions.height - margin.top - margin.bottom)
      .attr("fill", "rgb(250,250,251)");

    if (dataSeries) {
      canvas
        .on("pointerenter pointermove", pointermoved)
        .on("pointerleave", pointerleft)
        // Hide the tooltip if the browser takes over the gesture
        // (e.g. a vertical pan that scrolls the page).
        .on("pointercancel", pointerleft);
    }

    const [dataSeriesMin, dataSeriesMax] = d3.extent(
      (dataSeries ?? []).map(function (d) {
        return toDisplay(d.value);
      }),
    );

    let yScale = d3
      .scaleLinear()
      .domain([
        d3.min([toDisplay(yMin), dataSeriesMin]),
        d3.max([toDisplay(yMax), dataSeriesMax]),
      ])
      .range([dimensions.height - margin.bottom, margin.top]);

    const now = new Date();
    const fortyEightHoursAgo = new Date();
    fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);

    let xScale = d3
      .scaleTime()
      .domain([fortyEightHoursAgo, now])
      .range([margin.left, dimensions.width - margin.right]);

    // Actuation state fill: a band for the hours in which each actuation
    // was active (value >= 0.5). Drawn before the axes and data so it sits
    // behind them. Bands are drawn at the given opacity, so overlapping
    // bands blend darker.
    if (actuations) {
      for (const actuation of actuations) {
        const series = actuation.series ?? [];
        const onPeriods = [];
        let startTime = null;

        if (series.length > 0 && series[0].value !== 0) {
          startTime = new Date(series[0].time);
        }

        for (let i = 1; i < series.length; i++) {
          if (series[i].value >= 0.5 && series[i - 1].value < 0.5) {
            startTime = new Date(series[i].time);
          } else if (
            series[i].value < 0.5 &&
            series[i - 1].value >= 0.5 &&
            startTime
          ) {
            onPeriods.push({
              start: startTime,
              end: new Date(series[i].time),
            });
            startTime = null;
          }
        }

        if (startTime) {
          onPeriods.push({
            start: startTime,
            end: new Date(series[series.length - 1].time),
          });
        }

        canvas
          .append("g")
          .attr("class", "actuation-state")
          .selectAll("rect")
          .data(onPeriods)
          .join("rect")
          .attr("x", (d) => xScale(d.start))
          .attr("y", margin.top)
          .attr("width", (d) => xScale(d.end) - xScale(d.start))
          .attr("height", dimensions.height - margin.top - margin.bottom)
          .attr("fill", actuation.colour)
          .attr("opacity", actuation.opacity);
      }
    }

    const formatTime = (date) => {
      let hours = date.getHours();
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return hours + ampm;
    };
    const formatDate = d3.timeFormat("%b %d");

    // Add the x-axis.
    canvas
      .append("g")
      .attr("transform", `translate(0,${dimensions.height - margin.bottom})`)
      .attr("opacity", 0.7)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(dimensions.width / 80)
          .tickSizeOuter(0)
          .tickFormat((d, _i) => {
            if (d.getHours() === 0) {
              return formatDate(d);
            }
            return formatTime(d);
          }),
      )
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .selectAll(".tick text")
          .style("font-weight", (d) =>
            d.getHours() === 0 ? "bold" : "normal",
          ),
      );

    // Add the y-axis
    canvas
      .append("g")
      .attr("transform", `translate(${margin.left},0)`)
      .attr("opacity", 0.7)
      .call(d3.axisLeft(yScale).ticks(dimensions.height / 40))
      .call((g) => g.select(".domain").remove())
      .call((g) =>
        g
          .selectAll(".tick line")
          .attr("x2", dimensions.width - margin.left - margin.right)
          .attr("stroke-opacity", 0.1),
      )
      .call((g) =>
        g
          .append("text")
          .attr("transform", "rotate(-90)")
          .attr("font-size", 16)
          .attr("y", -40)
          .attr("x", -(dimensions.height / 2))
          .attr("text-anchor", "middle")
          .attr("fill", "currentColor")
          .attr("opacity", 0.9)
          .text(yLabel),
      );

    // Data curve
    if (dataSeries) {
      canvas
        .append("path")
        .datum(dataSeries)
        .attr(
          "d",
          d3
            .line()
            .defined((d) => d.value !== null)
            .x((d) => xScale(new Date(d.time)))
            .y((d) => yScale(toDisplay(d.value))),
        )
        .attr("fill", "none")
        .attr("stroke", colour)
        .attr("stroke-width", 2);
    }

    // Tooltip
    const tooltip = canvas.append("g");

    const formatTooltipDate = d3.timeFormat("%a %b %d %H:%M");

    const bisect = d3.bisector((d) => new Date(d.time)).center;
    function pointermoved(event) {
      const pointerDate = xScale.invert(d3.pointer(event)[0]);
      const i = bisect(dataSeries, pointerDate);
      const d = dataSeries[i];

      if (Math.abs(pointerDate - d.time) > 1000 * 60 * 5) {
        tooltip.style("display", "none");
        return;
      }

      const xPos = new Date(d.time);
      const yPos = toDisplay(d.value);

      if (d.value === null) {
        return;
      }

      const datalines = [
        formatTooltipDate(new Date(d.time)),
        formatValue(toDisplay(d.value)),
      ];

      // Show and position the tooltip
      tooltip.style("display", null);
      tooltip.attr("transform", `translate(${xScale(xPos)},${yScale(yPos)})`);

      const path = tooltip
        .selectAll("path")
        .data([null])
        .join("path")
        .attr("fill", "rgba(255,255,255,0.95)")
        .attr("stroke", "#ccc");

      const text = tooltip
        .selectAll("text")
        .data([null])
        .join("text")
        .call((text) =>
          text
            .selectAll("tspan")
            .data(datalines)
            .join("tspan")
            .attr("x", 0)
            .attr("y", (_, i) => `${i * 1.1}em`)
            .attr("font-weight", (_, i) => (i ? null : "bold"))
            .text((d) => d),
        );

      const offset = xScale(xPos) / dimensions.width - 0.5;
      size(text, path, -offset);
    }

    function pointerleft() {
      tooltip.style("display", "none");
    }

    // Wraps the text with a callout path of the correct size, as measured in the page.
    function size(text, path, offset) {
      const { y, width: w, height: h } = text.node().getBBox();
      offset *= w;

      text.attr("transform", `translate(${-w / 2 + offset},${15 - y})`);
      path.attr(
        "d",
        `M${-w / 2 - 10 + offset},5H-5l5,-5l5,5H${w / 2 + 10 + offset}v${h + 20}h-${w + 20}z`,
      );
    }
  }, [
    dataSeries,
    actuations,
    dimensions,
    yMin,
    yMax,
    yLabel,
    formatValue,
    colour,
    convertValue,
  ]);

  return (
    <div ref={wrapperRef} style={style}>
      <svg
        ref={svgRef}
        // On touch devices: let vertical drags scroll the page (the
        // chart previously called preventDefault on touchstart, which
        // trapped page scrolling) while horizontal drags still scrub
        // the tooltip via the pointer events above.
        style={{ width: "100%", height: "100%", touchAction: "pan-y" }}
      />
    </div>
  );
};
